import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  X, 
  Plus,
  Building,
  User,
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  FileText,
  Mail,
  Phone
} from 'lucide-react';
import { clientsData, enqueteursData, typesSinistres, naturesSinistres } from '../../data/mockData';

const DossierCreation = () => {
  const navigate = useNavigate();
  
  // État du formulaire
  const [formData, setFormData] = useState({
    // Informations de base
    clientId: '',
    typeSinistre: '',
    natureSinistre: '',
    dateSinistre: '',
    heureSinistre: '',
    lieu: '',
    codePostal: '',
    ville: '',
    description: '',
    montantEstime: '',
    priorite: 'normale',
    
    // Informations assuré
    typeAssure: 'particulier', // particulier ou societe
    assureNom: '',
    assurePrenom: '',
    assureDateNaissance: '',
    assureLieuNaissance: '',
    assureRaisonSociale: '',
    assureSiren: '',
    assureSiret: '',
    assureAdresse: '',
    assureCodePostal: '',
    assureVille: '',
    assureTelephone: '',
    assureEmail: '',
    
    // Informations tiers
    typeTiers: 'particulier', // particulier ou societe
    tiersNom: '',
    tiersPrenom: '',
    tiersDateNaissance: '',
    tiersLieuNaissance: '',
    tiersRaisonSociale: '',
    tiersSiren: '',
    tiersSiret: '',
    tiersAdresse: '',
    tiersCodePostal: '',
    tiersVille: '',
    tiersTelephone: '',
    tiersEmail: '',
    
    // Attribution
    enqueteurId: '',
    delai: '30', // Délai par défaut : 30 jours
    instructionsSpeciales: '',
    
    // Documents
    documentsTransmis: []
  });

  const [documents, setDocuments] = useState([
    { id: 1, nom: 'Lettre de mission', type: 'Lettre de mission', fichier: null },
    { id: 2, nom: 'Attestation de mission', type: 'Attestation de mission', fichier: null }
  ]);
  const [errors, setErrors] = useState({});

  // Gestion des changements de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Réinitialiser la nature du sinistre si le type change
    if (name === 'typeSinistre') {
      setFormData(prev => ({
        ...prev,
        natureSinistre: ''
      }));
    }
    
    // Réinitialiser les champs spécifiques si le type d'assuré change
    if (name === 'typeAssure') {
      if (value === 'particulier') {
        setFormData(prev => ({
          ...prev,
          assureRaisonSociale: '',
          assureSiren: '',
          assureSiret: ''
        }));
      } else if (value === 'societe') {
        setFormData(prev => ({
          ...prev,
          assureNom: '',
          assurePrenom: '',
          assureDateNaissance: '',
          assureLieuNaissance: ''
        }));
      }
    }
    
    // Réinitialiser les champs spécifiques si le type de tiers change
    if (name === 'typeTiers') {
      if (value === 'particulier') {
        setFormData(prev => ({
          ...prev,
          tiersRaisonSociale: '',
          tiersSiren: '',
          tiersSiret: ''
        }));
      } else if (value === 'societe') {
        setFormData(prev => ({
          ...prev,
          tiersNom: '',
          tiersPrenom: '',
          tiersDateNaissance: '',
          tiersLieuNaissance: ''
        }));
      }
    }
  };

  // Gestion des documents
  const handleDocumentAdd = () => {
    const newDoc = {
      id: Date.now(),
      nom: '',
      type: 'Autre',
      fichier: null
    };
    setDocuments(prev => [...prev, newDoc]);
  };

  const handleDocumentChange = (id, field, value) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === id ? { ...doc, [field]: value } : doc
    ));
  };

  const handleDocumentRemove = (id) => {
    // Empêcher la suppression des documents obligatoires (Lettre et Attestation de mission)
    const doc = documents.find(d => d.id === id);
    if (doc && (doc.type === 'Lettre de mission' || doc.type === 'Attestation de mission')) {
      alert('Ce document ne peut pas être supprimé. Il est requis pour l\'attribution du dossier.');
      return;
    }
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  // Vérifier si les documents obligatoires sont présents
  const hasRequiredDocuments = () => {
    const lettrePresente = documents.some(doc => 
      doc.type === 'Lettre de mission' && doc.fichier !== null
    );
    const attestationPresente = documents.some(doc => 
      doc.type === 'Attestation de mission' && doc.fichier !== null
    );
    return lettrePresente && attestationPresente;
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.clientId) newErrors.clientId = 'Client requis';
    if (!formData.typeSinistre) newErrors.typeSinistre = 'Type de sinistre requis';
    if (!formData.natureSinistre) newErrors.natureSinistre = 'Nature du sinistre requise';
    if (!formData.dateSinistre) newErrors.dateSinistre = 'Date du sinistre requise';
    if (!formData.lieu) newErrors.lieu = 'Lieu du sinistre requis';
    
    // Validation des formats (seulement si renseignés) pour l'assuré
    if (formData.assureSiren && !/^\d{9}$/.test(formData.assureSiren)) {
      newErrors.assureSiren = 'SIREN invalide (9 chiffres requis)';
    }
    if (formData.assureSiret && !/^\d{14}$/.test(formData.assureSiret)) {
      newErrors.assureSiret = 'SIRET invalide (14 chiffres requis)';
    }
    
    // Validation des formats (seulement si renseignés) pour le tiers
    if (formData.tiersSiren && !/^\d{9}$/.test(formData.tiersSiren)) {
      newErrors.tiersSiren = 'SIREN invalide (9 chiffres requis)';
    }
    if (formData.tiersSiret && !/^\d{14}$/.test(formData.tiersSiret)) {
      newErrors.tiersSiret = 'SIRET invalide (14 chiffres requis)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Vérifier les documents requis si un enquêteur est attribué
      if (formData.enqueteurId && !hasRequiredDocuments()) {
        alert('Impossible d\'attribuer le dossier : les documents requis (Lettre de mission et Attestation de mission) doivent être déposés.');
        return;
      }
      
      // Ici on ferait l'appel API pour créer le dossier
      console.log('Création du dossier:', formData, documents);
      
      // Simulation d'envoi d'email si enquêteur attribué
      if (formData.enqueteurId) {
        const enqueteur = enqueteursData.find(e => e.id === parseInt(formData.enqueteurId));
        const dateAttribution = new Date().toLocaleDateString('fr-FR');
        console.log(`Email envoyé à ${enqueteur.email}`);
        console.log(`Date d'attribution: ${dateAttribution}`);
        alert(`Dossier créé et attribué immédiatement à ${enqueteur.nom}.\nDate d'attribution: ${dateAttribution}\nEmail de notification envoyé.`);
      } else {
        alert('Dossier créé avec succès.');
      }
      
      navigate('/admin/dossiers');
    }
  };

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link 
            to="/admin/dossiers" 
            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-port-gray-900">Nouveau Dossier</h1>
            <p className="text-port-gray-600 mt-1">Création et attribution d'un nouveau dossier de sinistre</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations de base du sinistre */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Informations sur le Sinistre
            </h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label flex items-center">
                  <Building className="h-4 w-4 mr-1" />
                  Client *
                </label>
                <select 
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleChange}
                  className={`form-select ${errors.clientId ? 'border-red-500' : ''}`}
                >
                  <option value="">Sélectionner un client</option>
                  {clientsData.map(client => (
                    <option key={client.id} value={client.id}>{client.raisonSociale}</option>
                  ))}
                </select>
                {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Type de Sinistre *</label>
                <select 
                  name="typeSinistre"
                  value={formData.typeSinistre}
                  onChange={handleChange}
                  className={`form-select ${errors.typeSinistre ? 'border-red-500' : ''}`}
                >
                  <option value="">Sélectionner un type</option>
                  {typesSinistres.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.typeSinistre && <p className="text-red-500 text-sm mt-1">{errors.typeSinistre}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Référence du Sinistre</label>
                <input 
                  type="text"
                  name="referenceSinistre"
                  value={formData.referenceSinistre}
                  onChange={handleChange}
                  placeholder="Référence du sinistre"
                  className={`form-input ${errors.referenceSinistre ? 'border-red-500' : ''}`}
                />
                {errors.referenceSinistre && <p className="text-red-500 text-sm mt-1">{errors.referenceSinistre}</p>}
              </div>

              {/* Référence Interne, correspond a l'id du dossier dans la base de données, ne doit pas etre modifiable */}
              <div className="form-group">
                <label className="form-label">Référence Interne (automatique</label>
                <input 
                  type="text"
                  name="idDossier"
                  value={formData.idDossier}
                  onChange={handleChange}
                  placeholder="Référence interne"
                  className={`form-input ${errors.idDossier ? 'border-red-500' : ''}`}
                  disabled
                />
                {errors.idDossier && <p className="text-red-500 text-sm mt-1">{errors.idDossier}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Nature du Sinistre</label>
                <input 
                  type="text"
                  name="natureSinistre"
                  value={formData.natureSinistre}
                  onChange={handleChange}
                  placeholder="Nature du sinistre"
                  className={`form-input ${errors.natureSinistre ? 'border-red-500' : ''}`}
                />
                {errors.natureSinistre && <p className="text-red-500 text-sm mt-1">{errors.natureSinistre}</p>}
              </div>


              <div className="form-group">
                <label className="form-label">Nature du Sinistre</label>
                <input 
                  type="text"
                  name="natureSinistre"
                  value={formData.natureSinistre}
                  onChange={handleChange}
                  placeholder="Nature du sinistre"
                  className={`form-input ${errors.natureSinistre ? 'border-red-500' : ''}`}
                />
                {errors.natureSinistre && <p className="text-red-500 text-sm mt-1">{errors.natureSinistre}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Priorité</label>
                <select 
                  name="priorite"
                  value={formData.priorite}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Date du Sinistre *
                </label>
                <input 
                  type="date"
                  name="dateSinistre"
                  value={formData.dateSinistre}
                  onChange={handleChange}
                  className={`form-input ${errors.dateSinistre ? 'border-red-500' : ''}`}
                />
                {errors.dateSinistre && <p className="text-red-500 text-sm mt-1">{errors.dateSinistre}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Heure du Sinistre</label>
                <input 
                  type="time"
                  name="heureSinistre"
                  value={formData.heureSinistre}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Lieu du Sinistre *
                </label>
                <input 
                  type="text"
                  name="lieu"
                  value={formData.lieu}
                  onChange={handleChange}
                  placeholder="Adresse complète du sinistre"
                  className={`form-input ${errors.lieu ? 'border-red-500' : ''}`}
                />
                {errors.lieu && <p className="text-red-500 text-sm mt-1">{errors.lieu}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">Montant Estimé (€)</label>
                <input 
                  type="number"
                  name="montantEstime"
                  value={formData.montantEstime}
                  onChange={handleChange}
                  placeholder="0"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description du Sinistre</label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Description détaillée des circonstances du sinistre..."
                className="form-textarea"
                rows="4"
              />
            </div>
          </div>
        </div>

        {/* Informations sur l'assuré */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium flex items-center">
              <User className="h-5 w-5 mr-2" />
              Informations sur l'Assuré
            </h3>
          </div>
          <div className="card-body">
            {/* Type d'assuré */}
            <div className="mb-6 pb-6 border-b border-port-gray-200">
              <label className="form-label">Type d'Assuré</label>
              <div className="flex space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="typeAssure"
                    value="particulier"
                    checked={formData.typeAssure === 'particulier'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-port-gray-700">Particulier</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="typeAssure"
                    value="societe"
                    checked={formData.typeAssure === 'societe'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-port-gray-700">Société</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Champs pour Particulier */}
              {formData.typeAssure === 'particulier' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Nom</label>
                    <input 
                      type="text"
                      name="assureNom"
                      value={formData.assureNom}
                      onChange={handleChange}
                      className={`form-input ${errors.assureNom ? 'border-red-500' : ''}`}
                    />
                    {errors.assureNom && <p className="text-red-500 text-sm mt-1">{errors.assureNom}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Prénom</label>
                    <input 
                      type="text"
                      name="assurePrenom"
                      value={formData.assurePrenom}
                      onChange={handleChange}
                      className={`form-input ${errors.assurePrenom ? 'border-red-500' : ''}`}
                    />
                    {errors.assurePrenom && <p className="text-red-500 text-sm mt-1">{errors.assurePrenom}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Date de Naissance
                    </label>
                    <input 
                      type="date"
                      name="assureDateNaissance"
                      value={formData.assureDateNaissance}
                      onChange={handleChange}
                      className={`form-input ${errors.assureDateNaissance ? 'border-red-500' : ''}`}
                    />
                    {errors.assureDateNaissance && <p className="text-red-500 text-sm mt-1">{errors.assureDateNaissance}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Lieu de Naissance
                    </label>
                    <input 
                      type="text"
                      name="assureLieuNaissance"
                      value={formData.assureLieuNaissance}
                      onChange={handleChange}
                      placeholder="Ville, Pays"
                      className={`form-input ${errors.assureLieuNaissance ? 'border-red-500' : ''}`}
                    />
                    {errors.assureLieuNaissance && <p className="text-red-500 text-sm mt-1">{errors.assureLieuNaissance}</p>}
                  </div>
                </>
              )}

              {/* Champs pour Société */}
              {formData.typeAssure === 'societe' && (
                <>
                  <div className="form-group md:col-span-2">
                    <label className="form-label flex items-center">
                      <Building className="h-4 w-4 mr-1" />
                      Raison Sociale
                    </label>
                    <input 
                      type="text"
                      name="assureRaisonSociale"
                      value={formData.assureRaisonSociale}
                      onChange={handleChange}
                      className={`form-input ${errors.assureRaisonSociale ? 'border-red-500' : ''}`}
                    />
                    {errors.assureRaisonSociale && <p className="text-red-500 text-sm mt-1">{errors.assureRaisonSociale}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">SIREN</label>
                    <input 
                      type="text"
                      name="assureSiren"
                      value={formData.assureSiren}
                      onChange={handleChange}
                      placeholder="9 chiffres"
                      maxLength="9"
                      className={`form-input ${errors.assureSiren ? 'border-red-500' : ''}`}
                    />
                    {errors.assureSiren && <p className="text-red-500 text-sm mt-1">{errors.assureSiren}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">SIRET</label>
                    <input 
                      type="text"
                      name="assureSiret"
                      value={formData.assureSiret}
                      onChange={handleChange}
                      placeholder="14 chiffres"
                      maxLength="14"
                      className={`form-input ${errors.assureSiret ? 'border-red-500' : ''}`}
                    />
                    {errors.assureSiret && <p className="text-red-500 text-sm mt-1">{errors.assureSiret}</p>}
                  </div>
                </>
              )}

              {/* Champs communs */}
              <div className="form-group">
                <label className="form-label flex items-center">
                  <Phone className="h-4 w-4 mr-1" />
                  Téléphone
                </label>
                <input 
                  type="tel"
                  name="assureTelephone"
                  value={formData.assureTelephone}
                  onChange={handleChange}
                  className={`form-input ${errors.assureTelephone ? 'border-red-500' : ''}`}
                />
                {errors.assureTelephone && <p className="text-red-500 text-sm mt-1">{errors.assureTelephone}</p>}
              </div>

              <div className="form-group">
                <label className="form-label flex items-center">
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </label>
                <input 
                  type="email"
                  name="assureEmail"
                  value={formData.assureEmail}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group md:col-span-2">
                <label className="form-label">Adresse</label>
                <input 
                  type="text"
                  name="assureAdresse"
                  value={formData.assureAdresse}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Code Postal</label>
                <input 
                  type="text"
                  name="assureCodePostal"
                  value={formData.assureCodePostal}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Ville</label>
                <input 
                  type="text"
                  name="assureVille"
                  value={formData.assureVille}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Informations sur le tiers */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium flex items-center">
              <User className="h-5 w-5 mr-2" />
              Informations sur le Tiers
            </h3>
          </div>
          <div className="card-body">
            {/* Type de tiers */}
            <div className="mb-6 pb-6 border-b border-port-gray-200">
              <label className="form-label">Type de Tiers</label>
              <div className="flex space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="typeTiers"
                    value="particulier"
                    checked={formData.typeTiers === 'particulier'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-port-gray-700">Particulier</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="typeTiers"
                    value="societe"
                    checked={formData.typeTiers === 'societe'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-port-gray-700">Société</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Champs pour Particulier */}
              {formData.typeTiers === 'particulier' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Nom</label>
                    <input 
                      type="text"
                      name="tiersNom"
                      value={formData.tiersNom}
                      onChange={handleChange}
                      className={`form-input ${errors.tiersNom ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersNom && <p className="text-red-500 text-sm mt-1">{errors.tiersNom}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Prénom</label>
                    <input 
                      type="text"
                      name="tiersPrenom"
                      value={formData.tiersPrenom}
                      onChange={handleChange}
                      className={`form-input ${errors.tiersPrenom ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersPrenom && <p className="text-red-500 text-sm mt-1">{errors.tiersPrenom}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Date de Naissance
                    </label>
                    <input 
                      type="date"
                      name="tiersDateNaissance"
                      value={formData.tiersDateNaissance}
                      onChange={handleChange}
                      className={`form-input ${errors.tiersDateNaissance ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersDateNaissance && <p className="text-red-500 text-sm mt-1">{errors.tiersDateNaissance}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Lieu de Naissance
                    </label>
                    <input 
                      type="text"
                      name="tiersLieuNaissance"
                      value={formData.tiersLieuNaissance}
                      onChange={handleChange}
                      placeholder="Ville, Pays"
                      className={`form-input ${errors.tiersLieuNaissance ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersLieuNaissance && <p className="text-red-500 text-sm mt-1">{errors.tiersLieuNaissance}</p>}
                  </div>
                </>
              )}

              {/* Champs pour Société */}
              {formData.typeTiers === 'societe' && (
                <>
                  <div className="form-group md:col-span-2">
                    <label className="form-label flex items-center">
                      <Building className="h-4 w-4 mr-1" />
                      Raison Sociale
                    </label>
                    <input 
                      type="text"
                      name="tiersRaisonSociale"
                      value={formData.tiersRaisonSociale}
                      onChange={handleChange}
                      className={`form-input ${errors.tiersRaisonSociale ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersRaisonSociale && <p className="text-red-500 text-sm mt-1">{errors.tiersRaisonSociale}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">SIREN</label>
                    <input 
                      type="text"
                      name="tiersSiren"
                      value={formData.tiersSiren}
                      onChange={handleChange}
                      placeholder="9 chiffres"
                      maxLength="9"
                      className={`form-input ${errors.tiersSiren ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersSiren && <p className="text-red-500 text-sm mt-1">{errors.tiersSiren}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">SIRET</label>
                    <input 
                      type="text"
                      name="tiersSiret"
                      value={formData.tiersSiret}
                      onChange={handleChange}
                      placeholder="14 chiffres"
                      maxLength="14"
                      className={`form-input ${errors.tiersSiret ? 'border-red-500' : ''}`}
                    />
                    {errors.tiersSiret && <p className="text-red-500 text-sm mt-1">{errors.tiersSiret}</p>}
                  </div>
                </>
              )}

              {/* Champs communs */}
              <div className="form-group">
                <label className="form-label flex items-center">
                  <Phone className="h-4 w-4 mr-1" />
                  Téléphone
                </label>
                <input 
                  type="tel"
                  name="tiersTelephone"
                  value={formData.tiersTelephone}
                  onChange={handleChange}
                  className={`form-input ${errors.tiersTelephone ? 'border-red-500' : ''}`}
                />
                {errors.tiersTelephone && <p className="text-red-500 text-sm mt-1">{errors.tiersTelephone}</p>}
              </div>

              <div className="form-group">
                <label className="form-label flex items-center">
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </label>
                <input 
                  type="email"
                  name="tiersEmail"
                  value={formData.tiersEmail}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group md:col-span-2">
                <label className="form-label">Adresse</label>
                <input 
                  type="text"
                  name="tiersAdresse"
                  value={formData.tiersAdresse}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Code Postal</label>
                <input 
                  type="text"
                  name="tiersCodePostal"
                  value={formData.tiersCodePostal}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Ville</label>
                <input 
                  type="text"
                  name="tiersVille"
                  value={formData.tiersVille}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Documents du dossier */}
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium flex items-center">
                <Upload className="h-5 w-5 mr-2" />
                Documents du Dossier
              </h3>
              <button
                type="button"
                onClick={handleDocumentAdd}
                className="btn btn-secondary btn-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {documents.map(doc => {
                const isRequired = doc.type === 'Lettre de mission' || doc.type === 'Attestation de mission';
                return (
                  <div key={doc.id} className={`flex items-center space-x-3 p-3 border rounded-lg ${
                    isRequired ? 'border-atl-blue-300 bg-atl-blue-50' : 'border-port-gray-200'
                  }`}>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Nom du document"
                        value={doc.nom}
                        onChange={(e) => handleDocumentChange(doc.id, 'nom', e.target.value)}
                        className="form-input"
                        readOnly={isRequired}
                      />
                      <select
                        value={doc.type}
                        onChange={(e) => handleDocumentChange(doc.id, 'type', e.target.value)}
                        className="form-select"
                        disabled={isRequired}
                      >
                        <option value="Lettre de mission">Lettre de mission</option>
                        <option value="Attestation de mission">Attestation de mission</option>
                        <option value="Constat amiable">Constat amiable</option>
                        <option value="Photos">Photos</option>
                        <option value="Devis">Devis</option>
                        <option value="Factures">Factures</option>
                        <option value="Rapport police">Rapport police</option>
                        <option value="Certificat médical">Certificat médical</option>
                        <option value="Autre">Autre</option>
                      </select>
                      <div className="flex items-center">
                        <label className="btn btn-secondary btn-sm cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" />
                          {doc.fichier ? 'Fichier ajouté' : 'Choisir un fichier'}
                          <input
                            type="file"
                            onChange={(e) => handleDocumentChange(doc.id, 'fichier', e.target.files[0])}
                            className="hidden"
                          />
                        </label>
                        {isRequired && (
                          <span className="ml-2 text-xs text-atl-blue-700 font-medium">
                            Requis pour attribution
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDocumentRemove(doc.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        isRequired 
                          ? 'text-port-gray-400 cursor-not-allowed' 
                          : 'text-red-600 hover:bg-red-100'
                      }`}
                      disabled={isRequired}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Attribution */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium flex items-center">
              <User className="h-5 w-5 mr-2" />
              Attribution du Dossier
            </h3>
          </div>
          <div className="card-body">
            {/* Alerte documents requis */}
            {!hasRequiredDocuments() && (
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Documents requis manquants
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Pour attribuer ce dossier à un enquêteur, vous devez d'abord déposer les documents suivants :
                    </p>
                    <ul className="list-disc list-inside text-sm text-yellow-700 mt-2">
                      {!documents.some(doc => doc.type === 'Lettre de mission' && doc.fichier) && (
                        <li>Lettre de mission</li>
                      )}
                      {!documents.some(doc => doc.type === 'Attestation de mission' && doc.fichier) && (
                        <li>Attestation de mission</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="form-group">
                <label className="form-label">Enquêteur</label>
                <select 
                  name="enqueteurId"
                  value={formData.enqueteurId}
                  onChange={handleChange}
                  className="form-select"
                  disabled={!hasRequiredDocuments()}
                >
                  <option value="">Attribuer plus tard</option>
                  {enqueteursData.map(enqueteur => (
                    <option key={enqueteur.id} value={enqueteur.id}>
                      {enqueteur.nom} ({enqueteur.email})
                    </option>
                  ))}
                </select>
                {!hasRequiredDocuments() && (
                  <p className="text-sm text-port-gray-500 mt-1">
                    Disponible après ajout des documents requis
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Délai
                </label>
                <select 
                  name="delai"
                  value={formData.delai}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="15">15 jours</option>
                  <option value="30">30 jours</option>
                  <option value="35">35 jours</option>
                </select>
                <p className="text-sm text-port-gray-500 mt-1">
                  Délai prévu pour l'enquête
                </p>
              </div>

              <div className="form-group">
                <label className="form-label flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Date d'Attribution
                </label>
                <input 
                  type="text"
                  value={new Date().toLocaleDateString('fr-FR')}
                  className="form-input bg-port-gray-100"
                  disabled
                />
                <p className="text-sm text-port-gray-500 mt-1">
                  L'attribution est immédiate (date du jour)
                </p>
              </div>

              <div className="form-group md:col-span-3">
                <label className="form-label">Instructions Spéciales</label>
                <textarea 
                  name="instructionsSpeciales"
                  value={formData.instructionsSpeciales}
                  onChange={handleChange}
                  placeholder="Instructions particulières pour l'enquêteur..."
                  className="form-textarea"
                  rows="3"
                />
              </div>
            </div>

            {formData.enqueteurId && hasRequiredDocuments() && (
              <div className="mt-4 p-4 bg-atl-green-50 rounded-lg border border-atl-green-200">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-atl-green-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-atl-green-800">
                      Attribution immédiate
                    </p>
                    <p className="text-sm text-atl-green-700 mt-1">
                      Le dossier sera attribué immédiatement à {enqueteursData.find(e => e.id === parseInt(formData.enqueteurId))?.nom}.
                      Un email de notification lui sera automatiquement envoyé.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4">
          <Link to="/admin/dossiers" className="btn btn-secondary">
            Annuler
          </Link>
          <button type="submit" className="btn btn-primary">
            <Save className="h-4 w-4 mr-2" />
            Créer le Dossier
          </button>
        </div>
      </form>
    </div>
  );
};

export default DossierCreation;
